import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { Field, reduxForm, submit } from 'redux-form';
import { useDDPSubscription } from '@zedoc/ddp-connector';
import Text from '../../common/components/base/Text';
import Stack from '../../common/components/primitives/Stack';
import { default as RecipientSelect } from '../../common/selectors/Recipient';
import { callMethod } from '../../common/utilsClient/ddp/actions';
import FormFieldText from '../../common/components/FormFieldText';
import { PARTICIPATION_STATE__ENDED } from '../../common/constants';
import { notifyError, notifySuccess } from '../../utils/notify';
import { makeJsonSchemaFormValidator } from '../../utils/errors';
import {
  apiZedocDischargeRecipient,
  patientDetails,
} from '../../common/api/zedoc';
import branding from '../../utils/branding';
import Dialog from '../Dialog';

export const form = 'DischargeRecipient';

const DischargeRecipientForm = reduxForm({
  form,
  validate: makeJsonSchemaFormValidator({
    type: 'object',
    required: ['comment'],
    properties: {
      comment: {
        type: 'string',
        title: 'Note content',
        minLength: 1,
      },
    },
  }),
})(({ handleSubmit, onSubmit }) => {
  const { t } = useTranslation();

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Field
        component={FormFieldText}
        name="comment"
        label={t('forms:note.label')}
        placeholder={t('forms:note.placeholder')}
        required
        autoFocus
        big
      />
    </form>
  );
});

const DischargeRecipient = ({
  projectId,
  recipientId,
  participationId,
  onCancel,
  visible,
}) => {
  useDDPSubscription(
    patientDetails.withParams({
      recipientId,
      projectId,
    }),
  );

  const recipient = useSelector(
    RecipientSelect.one().whereIdEquals(recipientId),
  );

  const { t } = useTranslation();
  const handleOnCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };
  const dispatch = useDispatch();
  const handleOnSubmit = ({ comment }) =>
    dispatch(
      callMethod(apiZedocDischargeRecipient, {
        participationId,
        comment,
        state: PARTICIPATION_STATE__ENDED,
      }),
    )
      .then(() => {
        notifySuccess(
          t('confirmations:dischargeRecipient.success', {
            context: branding,
          }),
          {
            renderOutsideModal: true,
          },
        )();
        handleOnCancel();
      })
      .catch(notifyError());

  return (
    <Dialog
      title={t('dischargeRecipients', {
        count: 1,
        context: branding,
      })}
      onCancel={handleOnCancel}
      okText={t('discharge')}
      onOk={() => dispatch(submit(form))}
      visible={visible}
    >
      <Stack>
        <Trans i18nKey="confirmations:dischargeRecipient.title">
          <Text.Span importance="high">
            {{
              recipientName: recipient?.getFullName(),
            }}
          </Text.Span>
        </Trans>
        <Text.Paragraph type="danger">
          <strong>{t('confirmations:dischargeRecipient.danger')}</strong>
        </Text.Paragraph>
        <DischargeRecipientForm onSubmit={handleOnSubmit} />
      </Stack>
    </Dialog>
  );
};

DischargeRecipient.propTypes = {
  projectId: PropTypes.string.isRequired,
  recipientId: PropTypes.string.isRequired,
  participationId: PropTypes.string.isRequired,
  visible: PropTypes.bool,
  onCancel: PropTypes.func,
};

DischargeRecipient.defaultProps = {
  visible: false,
  onCancel: () => {},
};

export default DischargeRecipient;
